import { CustomFieldRenderProps, f0FormField } from '@factorialco/f0-react'
import { NumberInput } from '@factorialco/f0-react/dist/experimental'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'

const SCHEDULE_VALUES = [
  'workinghours',
  'mixedhours',
  'notsure',
  'outsideworkinghours',
] as const

const PAYSLIP_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}))

export function buildTimingCostSchema({
  allowEmployeePayment,
  currency,
}: {
  allowEmployeePayment?: boolean
  currency: string
}) {
  return z.object({
    dateRange: f0FormField(
      z
        .object({
          from: z.date(),
          to: z.date(),
        })
        .optional(),
      {
        label: i18n.t('trainings.training_requests.create.dates'),
        fieldType: 'daterange',
        fromLabel: i18n.t('common.from'),
        toLabel: i18n.t('common.to'),
      },
    ),
    duration: f0FormField(z.number().optional(), {
      label: i18n.t(
        'trainings.training_requests.create.duration_hours_placeholder',
      ),
      fieldType: 'number',
      row: 'hours',
    }),
    minutes: f0FormField(z.number().optional(), {
      label: i18n.t(
        'trainings.training_requests.create.duration_minutes_placeholder',
      ),
      fieldType: 'number',
      row: 'hours',
    }),
    schedule: f0FormField(z.enum(SCHEDULE_VALUES).optional(), {
      label: i18n.t(
        'trainings.training_requests.create.training_schedule',
      ),
      placeholder: i18n.t(
        'trainings.training_requests.create.select_placeholder',
      ),
      fieldType: 'select',
      options: [
        {
          label: i18n.t(
            'trainings.training_requests.create.schedule_options.during_work_hours',
          ),
          value: 'workinghours',
        },
        {
          label: i18n.t(
            'trainings.training_requests.create.schedule_options.mixed_working_hours',
          ),
          value: 'mixedhours',
        },
        {
          label: i18n.t(
            'trainings.training_requests.create.schedule_options.not_sure',
          ),
          value: 'notsure',
        },
        {
          label: i18n.t(
            'trainings.training_requests.create.schedule_options.outside_working_hours',
          ),
          value: 'outsideworkinghours',
        },
      ],
      showSearchBox: true,
    }),
    cost: f0FormField(z.number().optional(), {
      label: i18n.t('trainings.training_requests.create.cost'),
      fieldType: 'custom',
      render: ({
        value,
        onChange,
        disabled,
      }: CustomFieldRenderProps<number | undefined>) => (
        <NumberInput
          label={i18n.t('trainings.training_requests.create.cost')}
          locale={i18n.locale}
          units={currency}
          value={value ?? null}
          onChange={(v: number | null) => onChange(v ?? undefined)}
          disabled={disabled}
          maxDecimals={2}
          size="md"
          placeholder="0"
        />
      ),
    }),
    subsidized: f0FormField(z.boolean().default(false), {
      label: i18n.t(
        'trainings.training_requests.create.subsidized_checkbox_text',
      ),
      fieldType: 'checkbox',
      renderIf: ({ values }: { values: Record<string, unknown> }) =>
        !allowEmployeePayment || !values.cost,
    }),
    paymentOption: f0FormField(z.string().optional(), {
      label: i18n.t(
        'trainings.training_requests.create.payment_options.label',
      ),
      fieldType: 'select',
      options: [
        {
          label: i18n.t(
            'trainings.training_requests.create.payment_options.from_salary.title',
          ),
          value: 'employee',
        },
        {
          label: i18n.t(
            'trainings.training_requests.create.payment_options.by_company.title',
          ),
          value: 'company',
        },
      ],
      renderIf: ({ values }: { values: Record<string, unknown> }) =>
        Boolean(allowEmployeePayment && values.cost),
    }),
    paymentInstallments: f0FormField(z.string().optional(), {
      label: i18n.t(
        'trainings.training_requests.create.number_of_payslips',
      ),
      fieldType: 'select',
      options: PAYSLIP_OPTIONS,
      showSearchBox: true,
      renderIf: ({ values }: { values: Record<string, unknown> }) =>
        Boolean(
          allowEmployeePayment &&
            values.cost &&
            values.paymentOption === 'employee',
        ),
    }),
  })
}

export const timingCostSection = () => ({
  title: i18n.t(
    'trainings.training_requests.create.timing_cost_step.title',
  ),
  description: i18n.t(
    'trainings.training_requests.create.timing_cost_step.description',
  ),
})

export const timingCostDefaults = (allowEmployeePayment?: boolean) => ({
  subsidized: false,
  paymentInstallments: allowEmployeePayment ? '1' : undefined,
})
